import { useCallback, useEffect, useState } from "react";
import { Card } from "@/shared/ui/Card";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { prettifyApiError } from "@/shared/api";
import {
  applyWpMatches,
  fetchWpCredentialsStatus,
  saveWpCredentials,
  syncWpOrders,
  type WpApplyResponse,
  type WpCredentialsStatus,
  type WpSupplierResult,
} from "../api";

// Sync N.Ord.Forn da WooCommerce, scoped a UN fornitore (tab Itamoby o
// Ityhome). Replica del flusso legacy a 2 step (tab_fornitori.py):
// 1. "Sync": scraping WooCommerce sul sito fornitore con cookie auth
//    (credenziali nel keyring backend) → match {reference → n. ordine}.
// 2. "Applica a ordini": scrive i match in ``Order.supplier_order_ref``.
// Davide vede i risultati nel mezzo prima di applicarli (anti-foot-gun).

interface WooSyncSectionProps {
  supplierId: string;
  supplierLabel: string;
}

export function WooSyncSection({ supplierId, supplierLabel }: WooSyncSectionProps) {
  const [cred, setCred] = useState<WpCredentialsStatus | null>(null);
  const [result, setResult] = useState<WpSupplierResult | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [savingCred, setSavingCred] = useState(false);
  const [lastApply, setLastApply] = useState<WpApplyResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [credDialog, setCredDialog] = useState<{ user: string; password: string } | null>(null);

  const refreshCredentials = useCallback(async () => {
    try {
      const rows = await fetchWpCredentialsStatus();
      setCred(rows.find((r) => r.supplier_id === supplierId) ?? null);
    } catch (err) {
      setError(prettifyApiError(err));
    }
  }, [supplierId]);

  useEffect(() => {
    void refreshCredentials();
  }, [refreshCredentials]);

  const isConfigured = !!cred && cred.user_configured && cred.password_configured;

  async function runSync() {
    setSyncing(true);
    setMessage(null);
    setError(null);
    setLastApply(null);
    try {
      const response = await syncWpOrders([supplierId], null);
      const own = response.results.find((r) => r.supplier_id === supplierId) ?? null;
      setResult(own);
      setMessage(
        response.total_matches === 0
          ? "Sync completata: nessun abbinamento trovato."
          : `Sync completata: ${response.total_matches} abbinamenti trovati.`,
      );
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setSyncing(false);
    }
  }

  async function applyAll() {
    if (!result || result.matches.length === 0) {
      setMessage("Nessun match da applicare.");
      return;
    }
    setApplying(true);
    setError(null);
    try {
      const response = await applyWpMatches(result.matches);
      setLastApply(response);
      setMessage(
        `Applicati ${response.applied} match agli ordini. ` +
          `Saltati: ${response.skipped_no_order} senza ordine locale, ` +
          `${response.skipped_already_set} con N.Ord.Forn gia' presente.`,
      );
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setApplying(false);
    }
  }

  async function saveCredDialog() {
    if (!credDialog || savingCred) return;
    setSavingCred(true);
    setError(null);
    try {
      await saveWpCredentials(supplierId, credDialog.user, credDialog.password);
      setCredDialog(null);
      setMessage(`Credenziali ${supplierLabel} salvate.`);
      await refreshCredentials();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setSavingCred(false);
    }
  }

  const totalMatches = result?.matches.length ?? 0;

  return (
    <Card className="mb-4 p-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-display text-[16px] font-bold text-ink">
          Sync N.Ord.Forn da WooCommerce
        </h2>
        <div className="flex items-center gap-2">
          <span
            className={["h-2 w-2 rounded-full", isConfigured ? "bg-green" : "bg-amber"].join(" ")}
            aria-hidden="true"
          />
          <span className="tabular-nums text-[10.5px] text-ink-2">
            {isConfigured ? "credenziali configurate" : "credenziali mancanti"}
          </span>
          <button
            type="button"
            onClick={() => setCredDialog({ user: "", password: "" })}
            className="rounded-full border border-line bg-card px-2.5 py-1 text-[11.5px] text-ink-2 hover:border-accent"
          >
            {isConfigured ? "Aggiorna credenziali" : "Configura credenziali"}
          </button>
        </div>
      </div>
      <p className="text-[12.5px] text-ink-2 leading-relaxed mb-3">
        Lo scraper fa login sul portale {supplierLabel} (credenziali fuori dal database: keyring sul
        PC in sviluppo, secret file sul server), naviga la lista ordini (max 20 pagine), legge il
        campo "Nota" di ogni ordine e lo confronta con la reference Poleepo. I match NON vengono
        applicati automaticamente: clicca "Applica a ordini" per scriverli in DB.
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void runSync()}
          disabled={!isConfigured || syncing}
          className="rounded-full border border-ink bg-ink px-3 py-1.5 text-[12.5px] font-medium text-on-ink disabled:opacity-50"
        >
          {syncing ? `Sincronizzo ${supplierLabel}...` : `Sync ${supplierLabel}`}
        </button>
        {result && (
          <button
            type="button"
            onClick={() => void applyAll()}
            disabled={totalMatches === 0 || applying}
            className="rounded-full border border-green bg-green px-3 py-1.5 text-[12.5px] font-medium text-white disabled:opacity-50"
          >
            {applying ? "Applico..." : `Applica ${totalMatches} a ordini`}
          </button>
        )}
      </div>

      {message && (
        <div className="mt-3 rounded-full border border-blue/30 bg-blue-soft px-3 py-2 text-[12.5px] text-ink">
          {message}
        </div>
      )}
      <ErrorBanner message={error} className="mt-3" />

      {result && (
        <div className="mt-3">
          <ResultBlock result={result} />
        </div>
      )}

      {lastApply && (
        <div className="mt-3 rounded-full border border-line bg-paper-2 px-3 py-2 text-[11.5px] text-ink-2">
          Ultimo apply: <b>{lastApply.applied}</b> applicati · <b>{lastApply.skipped_no_order}</b>{" "}
          senza ordine locale · <b>{lastApply.skipped_already_set}</b> già impostati
        </div>
      )}

      {credDialog && (
        <CredentialsDialog
          supplierLabel={supplierLabel}
          user={credDialog.user}
          password={credDialog.password}
          saving={savingCred}
          onUserChange={(v) => setCredDialog((prev) => (prev ? { ...prev, user: v } : prev))}
          onPasswordChange={(v) =>
            setCredDialog((prev) => (prev ? { ...prev, password: v } : prev))
          }
          onSave={() => void saveCredDialog()}
          onCancel={() => setCredDialog(null)}
        />
      )}
    </Card>
  );
}

function ResultBlock({ result }: { result: WpSupplierResult }) {
  return (
    <div className="rounded-lg border border-line bg-paper px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <strong className="text-[13px] font-medium text-ink">{result.label}</strong>
          <span className="tabular-nums text-[10.5px] text-ink-2">
            {result.pages_scanned} pagine scansionate · {result.matches.length} match
          </span>
        </div>
        {result.error && (
          <span className="rounded-full bg-red-soft px-2 py-0.5 text-[10.5px] text-ink">
            {result.error}
          </span>
        )}
      </div>
      {result.matches.length === 0 ? (
        <p className="text-[11.5px] italic text-ink-2">Nessun abbinamento</p>
      ) : (
        <ul className="space-y-0.5 tabular-nums text-[11.5px] text-ink-2">
          {result.matches.slice(0, 20).map((m) => (
            <li key={m.reference}>
              {m.reference} → {m.supplier_order_number}
            </li>
          ))}
          {result.matches.length > 20 && (
            <li className="italic text-ink-2">... e altri {result.matches.length - 20}</li>
          )}
        </ul>
      )}
    </div>
  );
}

interface CredDialogProps {
  supplierLabel: string;
  user: string;
  password: string;
  /** Salvataggio in corso → disabilita Salva/Invio (feedback + no doppio submit). */
  saving: boolean;
  onUserChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

function CredentialsDialog({
  supplierLabel,
  user,
  password,
  saving,
  onUserChange,
  onPasswordChange,
  onSave,
  onCancel,
}: CredDialogProps) {
  const canSave = user.trim().length > 0 && password.length > 0 && !saving;
  // A11y (audit 2026-05-13 HIGH-FE3): Escape chiude, aria-labelledby sul
  // titolo, autoFocus sul primo input. Focus trap completo = libreria
  // dedicata, fuori scope.
  // Chiusure implicite (Esc, click sul backdrop): senza la guardia ``saving``
  // il form spariva con la POST ancora in volo e l'esito finiva in una card
  // che l'utente non stava piu' guardando, credenziali gia' perse.
  const closeIfIdle = useCallback(() => {
    if (!saving) onCancel();
  }, [saving, onCancel]);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") closeIfIdle();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [closeIfIdle]);

  const titleId = `cred-dialog-title-${supplierLabel}`;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40"
      onClick={closeIfIdle}
    >
      <div
        className="w-[420px] rounded-2xl border border-line bg-card p-5 shadow-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={titleId} className="font-display text-[16px] font-bold text-ink mb-2">
          Credenziali {supplierLabel}
        </h3>
        <p className="text-[11.5px] text-ink-2 mb-3 leading-relaxed">
          User e password vengono salvati fuori dal database (keyring sul PC in sviluppo, secret
          file sul server). Il backend li legge solo per fare login al portale WooCommerce. Premi{" "}
          <kbd>Esc</kbd> per annullare.
        </p>
        <label className="block mb-3">
          <span className="block text-[11.5px] font-medium text-ink-2 mb-1">Email login</span>
          <input
            type="email"
            value={user}
            onChange={(e) => onUserChange(e.target.value)}
            placeholder="ecommerce@bscom.it"
            disabled={saving}
            className="w-full rounded-2xl border border-line bg-card px-3 py-1.5 text-[12.5px] disabled:opacity-60"
            autoFocus
          />
        </label>
        <label className="block mb-4">
          <span className="block text-[11.5px] font-medium text-ink-2 mb-1">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            onKeyDown={(e) => {
              // ``canSave`` include gia' ``!saving`` → niente doppio invio via Invio.
              if (e.key === "Enter" && canSave) onSave();
            }}
            disabled={saving}
            className="w-full rounded-2xl border border-line bg-card px-3 py-1.5 text-[12.5px] tabular-nums disabled:opacity-60"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-full border border-line bg-card px-3 py-1.5 text-[12px] text-ink-2 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            className="rounded-full border border-ink bg-ink px-3 py-1.5 text-[12px] text-on-ink disabled:opacity-50"
          >
            {saving ? "Salvo…" : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}
